import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  processPaymentLinking,
  fetchSavedMethods,
} from "../../redux/slices/paymentLinkingSlice";
import { generateIdempotencyKey, validateCardNumber } from "../utils/formatters";
import { PaymentMethodHeader, PaymentMethodCard, CreditCardForm } from "../components";
import ThreeDSModal from "../components/ThreeDSModal";

const PaymentMethodSelection = () => {
  const { linkCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { paymentLink, transaction, savedMethods, allowNewMethod, loading, error } = useSelector(
    (state) => ({
      paymentLink: state.paymentLinking.paymentLink,
      transaction: state.paymentLinking.transaction,
      savedMethods: state.paymentLinking.savedMethods,
      allowNewMethod: state.paymentLinking.allowNewMethod,
      loading: {
        process: state.paymentLinking.loading.process,
        savedMethods: state.paymentLinking.loading.savedMethods,
      },
      error: state.paymentLinking.error,
    })
  );

  const [selectedMethod, setSelectedMethod] = useState(null);
  const [expandedMethod, setExpandedMethod] = useState(null);
  const [show3DS, setShow3DS] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);

  // Card form state
  const [cardNumber, setCardNumber] = useState("");
  const [cardHolder, setCardHolder] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [showCvv, setShowCvv] = useState(false);
  const [saveCard, setSaveCard] = useState(false);

  // Billing address state
  const [address, setAddress] = useState({
    addressLine1: "",
    city: "",
    state: "",
    postalCode: "",
    countryCode: "ID",
  });

  // Customer details state
  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // Form errors
  const [formErrors, setFormErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!paymentLink) {
      navigate(`/b2b/linking/${linkCode}`);
      return;
    }

    // Pre-fill customer details from payment link
    if (paymentLink.customer) {
      setCustomerDetails({
        name: paymentLink.customer.name || "",
        email: paymentLink.customer.email || "",
        phone: paymentLink.customer.phone || "",
      });
    }

    // Auto-save card if recurring or preauth
    const isRecurring = paymentLink.settings?.is_recurring;
    const isPreauth = paymentLink.settings?.is_preauth;
    if (isRecurring || isPreauth) {
      setSaveCard(true);
    }

    // Load saved methods if recurring
    if (isRecurring) {
      dispatch(fetchSavedMethods(linkCode));
    }
  }, [paymentLink, linkCode, navigate, dispatch]);

  useEffect(() => {
    if (!transaction) return;

    // 3DS required: payment_url is NicePay's HTML form (not a URL)
    if (transaction.payment_url) {
      setRedirectUrl(transaction.payment_url);
      setShow3DS(true);
      return;
    }

    // Extract transaction ID and effective status (recurring has nested shape)
    const txnId = transaction.transaction_id || transaction.initial_payment?.transaction_id;
    const effectiveStatus = transaction.status === 'recurring_setup'
      ? (transaction.initial_payment?.status || 'processing')
      : transaction.status;

    // Auto-navigate to result page; polling will run there if still pending/processing
    if (txnId) {
      navigate(`/b2b/linking/${linkCode}/result?transaction_id=${txnId}`, {
        state: { transactionId: txnId, initialStatus: effectiveStatus, transaction },
      });
    }
  }, [transaction, linkCode, navigate]);

  const handleMethodClick = (methodId) => {
    if (expandedMethod === methodId) {
      setExpandedMethod(null);
    } else {
      setExpandedMethod(methodId);
      setSelectedMethod(methodId);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Card validation
    if (!cardNumber || !validateCardNumber(cardNumber)) {
      errors.cardNumber = "Please enter a valid card number";
    }
    if (!cardHolder || cardHolder.trim().length < 3) {
      errors.cardHolder = "Please enter cardholder name";
    }
    if (!expiryMonth || !expiryYear) {
      errors.expiry = "Please enter expiry date";
    }
    if (!cvv || cvv.length < 3) {
      errors.cvv = "Please enter CVV";
    }

    // Billing address validation
    if (!address.addressLine1) errors.addressLine1 = "Address is required";
    if (!address.city) errors.city = "City is required";
    if (!address.state) errors.state = "State is required";
    if (!address.postalCode) errors.postalCode = "Postal code is required";

    // Customer details validation
    if (!customerDetails.name) errors.name = "Name is required";
    if (!customerDetails.email) errors.email = "Email is required";
    if (!customerDetails.phone) errors.phone = "Phone is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePayment = async () => {
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);
    const idempotencyKey = generateIdempotencyKey();

    const selectedMethodData = payment_methods?.find(m => m.method_id === selectedMethod);

    const paymentData = {
      payment_method_id: selectedMethod,
      payment_type: selectedMethodData?.type || 'credit_card',
      card_details: {
        card_number: cardNumber.replace(/\s/g, ""),
        card_holder_name: cardHolder,
        card_expiry_month: parseInt(expiryMonth),
        card_expiry_year: parseInt(expiryYear),
        card_cvv: cvv,
      },
      billing_address: {
        address_line1: address.addressLine1,
        city: address.city,
        state: address.state,
        postal_code: address.postalCode,
        country_code: address.countryCode,
      },
      save_card: saveCard,
      customer_details: {
        name: customerDetails.name,
        email: customerDetails.email,
        phone: customerDetails.phone,
      },
    };

    try {
      await dispatch(processPaymentLinking({ linkCode, paymentData, idempotencyKey })).unwrap();
    } catch (err) {
      console.error("Payment failed:", err);

      // Show user-friendly error message
      const errorMessage = err?.message || err?.data?.message || "Payment processing failed. Please try again.";
      setFormErrors({ submit: errorMessage });
    } finally {
      setIsProcessing(false);
    }
  };

  const handle3DSSuccess = () => {
    setShow3DS(false);
    const txnId = transaction?.transaction_id || transaction?.initial_payment?.transaction_id;
    navigate(`/b2b/linking/${linkCode}/result?transaction_id=${txnId}`, {
      state: { transactionId: txnId, initialStatus: 'pending', transaction, from3DS: true },
    });
  };

  const handle3DSFailure = (errorMessage) => {
    setShow3DS(false);
    setRedirectUrl(null);
    if (errorMessage) {
      setFormErrors({ submit: errorMessage });
    }
  };

  if (!paymentLink) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#F9FAFC" }}>
        <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#01793B", borderRightColor: "transparent" }} />
      </div>
    );
  }

  const { payment_methods, pricing, settings, merchant } = paymentLink;

  if (!payment_methods || !Array.isArray(payment_methods)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#F9FAFC" }}>
        <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#01793B", borderRightColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 sm:py-12 px-4" style={{ backgroundColor: "#F9FAFC" }}>
      <div className="max-w-2xl mx-auto">
        <PaymentMethodHeader
          merchant={merchant}
          pricing={pricing}
          onBack={() => navigate(`/b2b/linking/${linkCode}`)}
        />

        {/* Payment Methods */}
        <div className="space-y-3">
          {payment_methods.map((method) => (
            <PaymentMethodCard
              key={method.method_id}
              method={method}
              isExpanded={expandedMethod === method.method_id}
              onClick={() => handleMethodClick(method.method_id)}
            >
              {method.type === "ewallet" && (
                <div className="p-4 border-t border-slate-100">
                  <p className="text-xs sm:text-sm text-slate-600 mb-3">
                    You'll be redirected to the Gojek app to authorize the
                    link. Once linked, future bills are charged to your GoPay
                    account automatically.
                  </p>
                  <button
                    onClick={() => navigate(`/b2b/linking/${linkCode}/gopay`)}
                    className="w-full text-white font-semibold py-3 px-6 rounded-xl transition-all"
                    style={{ backgroundColor: "#01793B" }}
                  >
                    Continue with GoPay
                  </button>
                </div>
              )}
              {method.type === "credit_card" && (
                <CreditCardForm
                  method={method}
                  pricing={pricing}
                  cardNumber={cardNumber}
                  setCardNumber={setCardNumber}
                  cardHolder={cardHolder}
                  setCardHolder={setCardHolder}
                  expiryMonth={expiryMonth}
                  setExpiryMonth={setExpiryMonth}
                  expiryYear={expiryYear}
                  setExpiryYear={setExpiryYear}
                  cvv={cvv}
                  setCvv={setCvv}
                  showCvv={showCvv}
                  setShowCvv={setShowCvv}
                  address={address}
                  setAddress={setAddress}
                  customerDetails={customerDetails}
                  setCustomerDetails={setCustomerDetails}
                  saveCard={saveCard}
                  setSaveCard={setSaveCard}
                  formErrors={formErrors}
                  isProcessing={isProcessing}
                  onSubmit={handlePayment}
                  error={error}
                />
              )}
            </PaymentMethodCard>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-[10px] sm:text-xs" style={{ color: "#828282" }}>
            © 2025 {process.env.REACT_APP_NAME || "SingaEngine"}. All Rights Reserved.
          </p>
        </div>
      </div>

      {/* 3DS Modal */}
      <ThreeDSModal
        isOpen={show3DS}
        redirectUrl={redirectUrl}
        // linkCode + transactionId enable the modal's polling fallback
        // for vendors that don't postMessage on 3DS completion
        // (Midtrans). Without these the iframe stalls on Midtrans's
        // "Tap to Continue" page indefinitely.
        linkCode={linkCode}
        transactionId={transaction?.transaction_id || transaction?.initial_payment?.transaction_id}
        onClose={handle3DSFailure}
        onSuccess={handle3DSSuccess}
        onFailure={handle3DSFailure}
      />
    </div>
  );
};

export default PaymentMethodSelection;
